import React, { useState, useEffect, useRef, useCallback } from "react";
import { useWatch } from "react-hook-form";
import { Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormControl, FormItem, FormMessage } from "@/components/ui/form";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const DEBOUNCE_DELAY = 300;
const BLUR_DELAY = 200;
const MIN_SEARCH_LENGTH = 2;

const AutocompleteInput = ({
  control,
  fieldName,
  displayFieldName,
  label,
  required = false,
  placeholder,
  searchEndpoint,
  valueKey = "id",
  displayKey = "name",
  displayFormatter,
  className = "",
  field,
  fieldState,
  setValue,
  queryParamName = "q",
  responseTransformer,
}) => {
  const fieldValue = useWatch({ control, name: fieldName });
  const displayValue = useWatch({ control, name: displayFieldName });

  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceTimer = useRef(null);
  const abortControllerRef = useRef(null);

  // Reset state when field value is cleared
  useEffect(() => {
    if (!fieldValue) {
      setIsOpen(false);
      setResults([]);
      setLoading(false);
      setSelectedIndex(-1);
    }
  }, [fieldValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSearch = useCallback(
    async (query) => {
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (query.length < MIN_SEARCH_LENGTH) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      // Set new timer with debounce
      debounceTimer.current = setTimeout(async () => {
        try {
          // Create new AbortController for this request
          abortControllerRef.current = new AbortController();

          const response = await fetch(
            `${BACKEND_URL}${searchEndpoint}?${queryParamName}=${encodeURIComponent(query)}`,
            { signal: abortControllerRef.current.signal }
          );

          if (response.ok) {
            const data = await response.json();
            
            // Apply custom response transformer if provided
            let transformedData = data;
            if (responseTransformer && typeof responseTransformer === "function") {
              transformedData = responseTransformer(data);
            }
            
            // Handle both array responses and paginated responses with data property
            if (Array.isArray(transformedData)) {
              setResults(transformedData);
            } else if (transformedData && Array.isArray(transformedData.data)) {
              setResults(transformedData.data);
            } else {
              setResults([]);
            }
          } else {
            // Handle error responses
            const errorData = await response.json().catch(() => ({}));
            console.error(`Search failed for ${fieldName}:`, errorData);
            setResults([]);
          }
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error(`Error searching ${fieldName}:`, error);
            setResults([]);
          }
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_DELAY);
    },
    [fieldName, searchEndpoint]
  );

  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setValue(fieldName, value, { shouldValidate: false });
      setValue(displayFieldName, value, { shouldValidate: false });
      handleSearch(value);
      setIsOpen(true);
      setSelectedIndex(-1);
    },
    [fieldName, displayFieldName, handleSearch, setValue]
  );

  // Helper function to get display value from item
  const getDisplayValue = useCallback(
    (item) => {
      if (displayFormatter && typeof displayFormatter === "function") {
        return displayFormatter(item);
      }
      return item[displayKey] || item[valueKey] || "";
    },
    [displayKey, valueKey, displayFormatter]
  );

  const handleSelect = useCallback(
    (item) => {
      const selectedValue = item[valueKey] || "";
      const itemDisplayValue = getDisplayValue(item);

      setValue(fieldName, selectedValue, { shouldValidate: true });
      setValue(displayFieldName, itemDisplayValue, { shouldValidate: false });
      setIsOpen(false);
      setResults([]);
      setSelectedIndex(-1);
    },
    [fieldName, displayFieldName, valueKey, getDisplayValue, setValue]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || results.length === 0) return;

      const maxIndex = results.length - 1;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
      } else if (
        e.key === "Enter" &&
        selectedIndex >= 0 &&
        results[selectedIndex]
      ) {
        e.preventDefault();
        handleSelect(results[selectedIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    },
    [isOpen, results, selectedIndex, handleSelect]
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setIsOpen(false);
      setSelectedIndex(-1);
    }, BLUR_DELAY);
  }, []);

  const handleFocus = useCallback(() => {
    if (fieldValue && fieldValue.length >= MIN_SEARCH_LENGTH) {
      handleSearch(fieldValue);
      setIsOpen(true);
    }
  }, [fieldValue, handleSearch]);

  return (
    <FormItem>
      <Label>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </Label>
      <div className="relative">
        <FormControl>
          <Input
            {...field}
            value={displayValue || field.value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={handleFocus}
            className={`pr-10 ${className} ${
              fieldState.error
                ? "border-red-500 focus-visible:ring-red-500"
                : ""
            }`}
            placeholder={placeholder}
            autoComplete="off"
          />
        </FormControl>
        <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />

        {isOpen && (displayValue || field.value)?.length >= MIN_SEARCH_LENGTH && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                Searching...
              </div>
            ) : results.length > 0 ? (
              <ul className="py-1" role="listbox">
                {results.map((item, index) => (
                  <li
                    key={item[valueKey] || index}
                    role="option"
                    aria-selected={selectedIndex === index}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center justify-between ${
                      selectedIndex === index ? "bg-gray-100" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(item);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span>{getDisplayValue(item)}</span>
                    {fieldValue === item[valueKey] && (
                      <Check className="h-4 w-4 text-purple-600" />
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                No results found
              </div>
            )}
          </div>
        )}
      </div>
      <FormMessage />
    </FormItem>
  );
};

export default AutocompleteInput;
